import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import { db } from '@core/db/db'
import { httpErrorText } from '@core/utils/httpError'
import { CLOUD_API } from '@core/pages/admin/shared'
import { useAuthToken } from '@core/pages/admin/useAuthToken'
import { btn, btnBase, btnSize, cx, hint } from '@ui/classes'
import { credInput, errBanner, okBanner } from '@ui/admin'
import { CzJournalSection } from './CzJournalSection'
import { WithdrawalsSection } from './WithdrawalsSection'
import { CzCredentialsSection } from './CzCredentialsSection'

/** Больше групп контур не тянет: каждая = отдельный запрос при проверке. */
const MAX_GROUPS = 5

/** Ключ meta принадлежит модулю, а не ядру. */
const META_CZ_ONLINE = 'cz_online'

/** Ответ `GET /cz/status` — только то, что нужно для выбора групп. */
interface CzStatus {
  product_groups?: Array<{ name?: string; productGroup?: string } | string>
}

/**
 * Имена групп из ответа `/cz/status`.
 *
 * JWT ЧЗ отдаёт `product_group_info[].name`, participant — `productGroup`;
 * поддерживаем обе формы, иначе в реальном контуре список пустой.
 */
function readAvailableGroups(status: CzStatus | null): string[] {
  const raw = status?.product_groups ?? []
  const list: string[] = []
  for (const item of raw) {
    const pg = typeof item === 'string' ? item : (item?.name ?? item?.productGroup)
    if (typeof pg === 'string' && pg.trim()) list.push(pg.trim())
  }
  return [...new Set(list)]
}

/** Формат группы ЧЗ: латиница, цифры, дефис (как `normalizePg` на backend). */
const PG_PATTERN = /^[a-z][a-z0-9-]{1,24}$/

/**
 * Содержимое карточки «Честный Знак» в «Интеграциях».
 *
 * Включение модуля — галочка в заголовке `ModuleCard` ядра. Здесь остаётся
 * только режим проверки: онлайн (True API) или офлайн-буфер чека.
 */
export function CzModuleSection({
  core,
  canEdit = false
}: {
  core: AppCore
  canEdit?: boolean
}) {
  const token = useAuthToken()
  const [online, setOnline] = useState(true)
  const [note, setNote] = useState<string | null>(null)
  /** Выбранные группы (ещё не сохранённые) и сохранённые — для dirty-состояния. */
  const [groups, setGroups] = useState<string[]>([])
  const [savedGroups, setSavedGroups] = useState<string[]>([])
  /** Группы из токена ЧЗ: у участника есть договоры по ним. */
  const [available, setAvailable] = useState<string[]>([])
  const [groupsError, setGroupsError] = useState<string | null>(null)
  const [groupsBusy, setGroupsBusy] = useState(false)
  /** Ручной ввод группы: работает и без `/cz/status` (нет токена/договора). */
  const [manualGroup, setManualGroup] = useState('')
  const [statusBusy, setStatusBusy] = useState(false)
  /** МОД: КПП и ФИАС, нужны для LK_RECEIPT/WRITE_OFF. */
  const [kpp, setKpp] = useState('')
  const [fiasId, setFiasId] = useState('')
  const [savedKpp, setSavedKpp] = useState('')
  const [savedFiasId, setSavedFiasId] = useState('')
  const [modBusy, setModBusy] = useState(false)
  const [modError, setModError] = useState<string | null>(null)

  useEffect(() => {
    void db.meta
      .get(META_CZ_ONLINE)
      .then((entry) => setOnline(entry?.value === undefined ? true : Boolean(entry.value)))
      .catch(() => undefined)
  }, [])

  /** Настройки организации — источник истины для групп (65.12.8). */
  const loadGroups = useCallback(async () => {
    if (!token) return
    setGroupsError(null)
    try {
      const response = await fetch(`${CLOUD_API}/tenant/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        setGroupsError(httpErrorText('Настройки организации', response.status))
        return
      }
      const json = (await response.json()) as {
        settings?: { czProductGroups?: unknown; czMod?: { kpp?: unknown; fiasId?: unknown } }
      }
      const list = Array.isArray(json.settings?.czProductGroups)
        ? json.settings.czProductGroups.filter((item): item is string => typeof item === 'string')
        : []
      setGroups(list)
      setSavedGroups(list)
      const savedMod = json.settings?.czMod
      const loadedKpp = typeof savedMod?.kpp === 'string' ? savedMod.kpp : ''
      const loadedFias = typeof savedMod?.fiasId === 'string' ? savedMod.fiasId : ''
      setKpp(loadedKpp)
      setSavedKpp(loadedKpp)
      setFiasId(loadedFias)
      setSavedFiasId(loadedFias)
    } catch {
      setGroupsError('Нет связи с облаком — список групп недоступен')
    }
  }, [token])

  /**
   * Какие группы реально подключены у участника.
   *
   * Только по кнопке: `/cz/status` берёт токен ЧЗ, а его обновление подписывает
   * запрос УКЭП (cryptcp) — не хотим запускать это при каждом открытии админки.
   */
  const loadStatus = useCallback(async () => {
    if (!token) return
    setStatusBusy(true)
    try {
      const response = await fetch(`${CLOUD_API}/cz/status`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        setGroupsError(httpErrorText('Диагностика ЧЗ', response.status))
        return
      }
      setAvailable(readAvailableGroups((await response.json()) as CzStatus))
    } catch {
      setGroupsError('Не удалось получить список договоров из ЧЗ')
    } finally {
      setStatusBusy(false)
    }
  }, [token])

  useEffect(() => {
    void loadGroups()
  }, [loadGroups])

  /** Сохранённая группа могла исчезнуть из токена — не теряем её при сохранении. */
  const allGroups = useMemo(
    () => [...new Set([...available, ...groups])].sort((a, b) => a.localeCompare(b)),
    [available, groups]
  )

  const dirty = groups.join('|') !== savedGroups.join('|')
  const modDirty = kpp.trim() !== savedKpp || fiasId.trim() !== savedFiasId

  const toggleGroup = (pg: string, next: boolean) => {
    setGroups((prev) => {
      if (next) {
        if (prev.includes(pg) || prev.length >= MAX_GROUPS) return prev
        return [...prev, pg]
      }
      return prev.filter((item) => item !== pg)
    })
  }

  /** Добавить группу вручную (когда `/cz/status` недоступен). */
  const addManualGroup = () => {
    const pg = manualGroup.trim().toLowerCase()
    if (!PG_PATTERN.test(pg)) {
      setGroupsError('Группа: латиница, цифры и дефис (например, milk)')
      return
    }
    if (groups.includes(pg)) {
      setManualGroup('')
      return
    }
    if (groups.length >= MAX_GROUPS) {
      setGroupsError(`Не больше ${MAX_GROUPS} групп`)
      return
    }
    setGroupsError(null)
    setGroups([...groups, pg])
    setManualGroup('')
  }

  const saveGroups = async () => {
    if (!token) return
    setGroupsBusy(true)
    setGroupsError(null)
    setNote(null)
    try {
      const response = await fetch(`${CLOUD_API}/tenant/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ czProductGroups: groups })
      })
      if (!response.ok) {
        setGroupsError(httpErrorText('Настройки организации', response.status))
        return
      }
      const json = (await response.json()) as {
        settings?: { czProductGroups?: unknown }
      }
      const list = Array.isArray(json.settings?.czProductGroups)
        ? json.settings.czProductGroups.filter((item): item is string => typeof item === 'string')
        : groups
      setGroups(list)
      setSavedGroups(list)
      setNote(`Товарные группы сохранены: ${list.join(', ')}`)
    } catch {
      setGroupsError('Не удалось сохранить группы: нет связи с облаком')
    } finally {
      setGroupsBusy(false)
    }
  }

  const saveMod = async () => {
    if (!token) return
    const nextKpp = kpp.trim()
    const nextFias = fiasId.trim().toLowerCase()
    setModError(null)
    if (nextKpp && !/^\d{9}$/.test(nextKpp)) {
      setModError('КПП должен содержать 9 цифр')
      return
    }
    if (
      nextFias &&
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        nextFias
      )
    ) {
      setModError('Укажите ФИАС в формате UUID')
      return
    }
    setModBusy(true)
    try {
      const response = await fetch(`${CLOUD_API}/tenant/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ czMod: { kpp: nextKpp, fiasId: nextFias } })
      })
      if (!response.ok) {
        setModError(httpErrorText('Настройки МОД', response.status))
        return
      }
      const json = (await response.json()) as {
        settings?: { czMod?: { kpp?: string; fiasId?: string } }
      }
      const savedMod = json.settings?.czMod
      const savedKppValue = savedMod?.kpp ?? ''
      const savedFiasValue = savedMod?.fiasId ?? ''
      setKpp(savedKppValue)
      setSavedKpp(savedKppValue)
      setFiasId(savedFiasValue)
      setSavedFiasId(savedFiasValue)
      setNote('МОД сохранён')
    } catch {
      setModError('Не удалось сохранить МОД: нет связи с облаком')
    } finally {
      setModBusy(false)
    }
  }

  const toggleOnline = (next: boolean) => {
    setOnline(next)
    // Применяем в текущей сессии и сохраняем для следующих открытий кассы.
    core.marking?.setOnline(next)
    void db.meta.put({ key: META_CZ_ONLINE, value: next }).catch(() => undefined)
    setNote(`Проверка кодов: ${next ? 'онлайн' : 'офлайн (коды отправятся позже)'}`)
  }

  return (
    <>
      <CzCredentialsSection canEdit={canEdit} />
      <label className="flex cursor-pointer items-center gap-2.5">
        <input
          type="checkbox"
          checked={online}
          onChange={(event) => toggleOnline(event.target.checked)}
          data-testid="cz-online-toggle"
          className="h-4 w-4 cursor-pointer accent-ok"
        />
        <span>Проверять коды онлайн (True API)</span>
      </label>
      <p className={hint}>
        Если выключено — коды сохранятся в буфере чека и отправятся позже (офлайн).
      </p>

      <div className="mt-4 border-t border-line pt-3">
        <h4 className="mb-1 mt-0 text-base2 font-bold">Товарные группы</h4>
        <p className={hint}>
          Проверка сама определяет группу по коду. Список нужен, чтобы не опрашивать
          лишние группы: до {MAX_GROUPS}, каждая — отдельный запрос к ЧЗ.
        </p>

        {!token ? (
          <p className={hint}>
            Подключите облако в разделе «Интеграции», чтобы выбрать товарные группы.
          </p>
        ) : (
          <>
            {groupsError && <p className={errBanner}>{groupsError}</p>}

            <div className="mb-2 grid gap-1.5 sm:grid-cols-2">
              {allGroups.map((pg) => {
                const checked = groups.includes(pg)
                const disabled = !checked && groups.length >= MAX_GROUPS
                return (
                  <label
                    key={pg}
                    className={cx(
                      'flex items-center gap-2 text-small',
                      disabled ? 'cursor-not-allowed text-muted' : 'cursor-pointer'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled || groupsBusy}
                      onChange={(event) => toggleGroup(pg, event.target.checked)}
                      className="h-3.5 w-3.5 cursor-pointer accent-ok"
                    />
                    <span className="font-mono">{pg}</span>
                    {available.includes(pg) && <span className="text-muted">— договор есть</span>}
                  </label>
                )
              })}
            </div>

            {allGroups.length === 0 && (
              <p className={hint}>
                Список договоров не загружен. Нажмите «Проверить договоры» или добавьте
                группу вручную.
              </p>
            )}

            <div className="mb-2 flex items-center gap-2">
              <input
                value={manualGroup}
                onChange={(event) => setManualGroup(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    addManualGroup()
                  }
                }}
                placeholder="milk"
                className={cx(credInput, 'w-40 py-1 font-mono text-small')}
                aria-label="Добавить товарную группу"
              />
              <button
                type="button"
                onClick={addManualGroup}
                disabled={groupsBusy || !manualGroup.trim()}
                className={cx(btnBase, btn.secondary, btnSize.sm)}
              >
                Добавить
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void saveGroups()}
                disabled={!dirty || groupsBusy}
                className={cx(btnBase, btn.secondary, btnSize.sm)}
              >
                {groupsBusy ? 'Сохранение…' : 'Сохранить группы'}
              </button>
              <button
                type="button"
                onClick={() => void loadStatus()}
                disabled={statusBusy}
                className={cx(btnBase, btn.secondary, btnSize.sm)}
                title="Запросить у ЧЗ договоры участника (может потребовать подписи УКЭП)"
              >
                {statusBusy ? 'Проверка…' : 'Проверить договоры'}
              </button>
              <span className={hint}>
                {groups.length === 0
                  ? 'Пусто — будет использована группа по умолчанию (CZ_DEFAULT_PG)'
                  : groups.length >= MAX_GROUPS
                    ? `Достигнут лимит: ${MAX_GROUPS}`
                    : `Выбрано: ${groups.length} из ${MAX_GROUPS}`}
              </span>
            </div>
          </>
        )}
      </div>

      <div className="mt-4 border-t border-line pt-3">
        <h4 className="mb-1 mt-0 text-base2 font-bold">МОД для вывода из оборота</h4>
        <p className={hint}>
          КПП и ФИАС места осуществления деятельности нужны для документов
          LK_RECEIPT/WRITE_OFF в True API. Заполняются один раз для организации.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-small">
            <span>КПП</span>
            <input
              value={kpp}
              onChange={(event) => setKpp(event.target.value.replace(/\D/g, '').slice(0, 9))}
              placeholder="123456789"
              inputMode="numeric"
              className={cx(credInput, 'w-40 py-1 font-mono text-small')}
              disabled={!canEdit || modBusy}
            />
          </label>
          <label className="flex flex-col gap-1 text-small">
            <span>ФИАС</span>
            <input
              value={fiasId}
              onChange={(event) => setFiasId(event.target.value.trim())}
              placeholder="0c5b2444-70a0-4932-980c-b4dc0d3f02b5"
              className={cx(credInput, 'w-80 py-1 font-mono text-small')}
              disabled={!canEdit || modBusy}
            />
          </label>
          <button
            type="button"
            onClick={() => void saveMod()}
            disabled={!canEdit || !modDirty || modBusy}
            className={cx(btnBase, btn.secondary, btnSize.sm)}
          >
            {modBusy ? 'Сохранение…' : 'Сохранить МОД'}
          </button>
        </div>
        {modError && <p className={errBanner}>{modError}</p>}
      </div>

      <CzJournalSection />
      <WithdrawalsSection />

      {note && <p className={okBanner}>{note}</p>}
    </>
  )
}
